import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useEffect } from 'react';

interface CreateTrackingEventParams {
  order_id: string;
  event_type: 'pickup_confirmed' | 'in_transit' | 'delivered' | 'exception';
  latitude?: number;
  longitude?: number;
  photo_url?: string;
  notes?: string;
}

export interface TrackingEvent {
  id: string;
  order_id: string;
  event_type: 'pickup_confirmed' | 'in_transit' | 'delivered' | 'exception';
  event_time: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
  photo_url?: string;
  created_by?: string;
  created_at: string;
}

export const useTrackingEvents = (orderId?: string) => {
  const queryClient = useQueryClient();

  // Fetch tracking events for an order
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['tracking-events', orderId],
    queryFn: async () => {
      if (!orderId) return [];

      const { data, error } = await supabase
        .from('tracking_events')
        .select('*')
        .eq('order_id', orderId)
        .order('event_time', { ascending: false });

      if (error) throw error;
      return data as TrackingEvent[];
    },
    enabled: !!orderId,
  });

  // Realtime subscription
  useEffect(() => {
    if (!orderId) return;

    const channel = supabase
      .channel(`tracking-events-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tracking_events',
          filter: `order_id=eq.${orderId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['tracking-events', orderId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId, queryClient]);

  const createTrackingEvent = useMutation({
    mutationFn: async (data: CreateTrackingEventParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('tracking_events')
        .insert({
          order_id: data.order_id,
          event_type: data.event_type,
          latitude: data.latitude,
          longitude: data.longitude,
          photo_url: data.photo_url,
          notes: data.notes,
          scanned_by: user.id,
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Succès',
        description: 'Événement de suivi créé',
      });
      
      queryClient.invalidateQueries({ queryKey: ['tracking-events'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return { 
    events,
    isLoading,
    createTrackingEvent: createTrackingEvent.mutateAsync,
    isCreating: createTrackingEvent.isPending,
  };
};
